import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Spin, Switch, Table, Tag } from "antd";
import { toast } from "react-toastify";
import PermissionGate from "../commoncomponent/PermissionGate";
import {
  getAppVersionConfig,
  upsertAppVersionConfig,
  getAppVersionHistory,
} from "../../../service/api";

const PLATFORMS = [
  { key: "android", label: "Android" },
  { key: "ios", label: "iOS" },
];

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : "—");

const HISTORY_COLUMNS = [
  { title: "When", dataIndex: "createdAt", render: fmtDateTime },
  {
    title: "Platform",
    dataIndex: "platform",
    render: (v) => <Tag>{v === "ios" ? "iOS" : "Android"}</Tag>,
  },
  { title: "Latest", dataIndex: "latestVersion", render: (v) => v || "—" },
  { title: "Min Required", dataIndex: "minRequiredVersion", render: (v) => v || "—" },
  {
    title: "Gate",
    dataIndex: "isActive",
    render: (v) => (v ? <Tag color="blue">Active</Tag> : <Tag>Off</Tag>),
  },
  { title: "By", dataIndex: "updatedByEmail", render: (v) => v || "—" },
];

const EMPTY = {
  latestVersion: "",
  minRequiredVersion: "",
  storeUrl: "",
  releaseNotes: "",
  isActive: false,
};

const isValidVersion = (v) => v === "" || /^\d+(\.\d+)*$/.test(String(v).trim());

// Derive what state the platform is in so the card header tag is always accurate.
const getVersionState = (form, appVersion) => {
  if (!form.isActive) return { label: "GATE OFF", color: "default" };
  if (!form.latestVersion) return { label: "ACTIVE", color: "blue" };
  if (form.minRequiredVersion && appVersion && appVersion < form.minRequiredVersion)
    return { label: "HARD BLOCK", color: "red" };
  if (appVersion && appVersion < form.latestVersion)
    return { label: "SOFT UPDATE", color: "orange" };
  return { label: "UP TO DATE", color: "green" };
};

const AppVersionTab = () => {
  const [loading, setLoading] = useState(false);
  const [savingPlatform, setSavingPlatform] = useState(null);
  const [forms, setForms] = useState({
    android: { ...EMPTY },
    ios: { ...EMPTY },
  });
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await getAppVersionHistory({ page, limit: 20 });
      if (res?.status) {
        setHistory(res.data?.rows || []);
        setHistoryTotal(res.data?.total || 0);
        setHistoryPage(res.data?.page || page);
      }
    } catch (e) {
      // non-critical
    } finally {
      setHistoryLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAppVersionConfig();
      if (res?.status) {
        const data = res.data || {};
        setForms({
          android: { ...EMPTY, ...(data.android || {}) },
          ios: { ...EMPTY, ...(data.ios || {}) },
        });
      } else {
        toast.error(res?.message || "Load failed", { toastId: "appversion-error" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Load failed", {
        toastId: "appversion-error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHistory(1);
  }, []);

  const updateForm = (platform, patch) =>
    setForms((prev) => ({ ...prev, [platform]: { ...prev[platform], ...patch } }));

  const save = async (platform) => {
    if (savingPlatform) return;
    const form = forms[platform];
    if (!isValidVersion(form.latestVersion) || !isValidVersion(form.minRequiredVersion)) {
      toast.error("Versions must be dotted-numeric — e.g. 1.2.0", {
        toastId: "appversion-error",
      });
      return;
    }
    setSavingPlatform(platform);
    try {
      const res = await upsertAppVersionConfig({
        platform,
        latestVersion: (form.latestVersion || "").trim(),
        minRequiredVersion: (form.minRequiredVersion || "").trim(),
        forceUpdate: false,
        storeUrl: form.storeUrl || "",
        releaseNotes: form.releaseNotes || "",
        isActive: !!form.isActive,
      });
      if (res?.status) {
        toast.success(`${platform} app-version settings saved`);
        if (res.data) updateForm(platform, { ...EMPTY, ...res.data });
        loadHistory(1);
      } else {
        toast.error(res?.message || "Save failed", { toastId: "appversion-error" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", {
        toastId: "appversion-error",
      });
    } finally {
      setSavingPlatform(null);
    }
  };

  // Plain render function — not a nested component — to avoid remounting on
  // each keystroke (which would kill input focus). See GlobalMaintenanceTab.
  const renderPlatformCard = (platform, label) => {
    const form = forms[platform];
    const saving = savingPlatform === platform;
    const state = getVersionState(form);

    return (
      <div
        key={platform}
        className="card"
        style={{ flex: "1 1 360px", minWidth: 320, padding: 16, background: "white" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h5 style={{ margin: 0 }}>
            {label} <Tag color={state.color}>{state.label}</Tag>
          </h5>
          <PermissionGate module="maintenance" action="update">
            <Button type="primary" loading={saving} onClick={() => save(platform)}>
              Save {label}
            </Button>
          </PermissionGate>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          {/* Gate toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <label style={{ fontWeight: 600 }}>
              Update gate active
              <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 400 }}>
                When off, no update prompts are shown for {label}.
              </div>
            </label>
            <PermissionGate
              module="maintenance"
              action="update"
              fallback={<Switch checked={!!form.isActive} disabled />}
            >
              <Switch
                checked={!!form.isActive}
                onChange={(v) => updateForm(platform, { isActive: v })}
              />
            </PermissionGate>
          </div>

          {/* Version inputs */}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, opacity: 0.7 }}>Latest version</label>
              <Input
                value={form.latestVersion}
                placeholder="e.g. 1.2.0"
                onChange={(e) => updateForm(platform, { latestVersion: e.target.value })}
                status={
                  form.latestVersion && !isValidVersion(form.latestVersion) ? "error" : ""
                }
              />
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                Below this → soft prompt (dismissible)
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, opacity: 0.7 }}>Min required version</label>
              <Input
                value={form.minRequiredVersion}
                placeholder="e.g. 1.1.0"
                onChange={(e) =>
                  updateForm(platform, { minRequiredVersion: e.target.value })
                }
                status={
                  form.minRequiredVersion && !isValidVersion(form.minRequiredVersion)
                    ? "error"
                    : ""
                }
              />
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                Below this → hard block (cannot dismiss)
              </div>
            </div>
          </div>

          {/* Store URL */}
          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>Store URL</label>
            <Input
              value={form.storeUrl}
              maxLength={500}
              placeholder={
                platform === "ios"
                  ? "https://apps.apple.com/app/id..."
                  : "https://play.google.com/store/apps/details?id=..."
              }
              onChange={(e) => updateForm(platform, { storeUrl: e.target.value })}
            />
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
              "Update Now" deep link. Falls back to platform store search if blank.
            </div>
          </div>

          {/* Release notes */}
          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>
              Release notes (What&apos;s new)
            </label>
            <Input.TextArea
              value={form.releaseNotes}
              maxLength={2000}
              rows={3}
              placeholder="Shown to users inside the update prompt"
              onChange={(e) => updateForm(platform, { releaseNotes: e.target.value })}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <p style={{ opacity: 0.7, marginBottom: 8 }}>
        Control update prompts for Android and iOS independently.{" "}
        <strong>Latest version</strong> triggers a soft, dismissible nudge — users can
        tap &ldquo;I&apos;ll do it later&rdquo; and the prompt won&apos;t reappear until
        you publish a newer version. <strong>Min required version</strong> triggers a hard
        block — users cannot use the app at all until they update. Changes take effect
        within ~60 seconds even if users are actively in the app.
      </p>

      <div
        style={{
          background: "#f8f9fa",
          border: "1px solid #e8e8e8",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 16,
          maxWidth: 750,
          fontSize: 13,
        }}
      >
        <div style={{ display: "grid", gap: 6 }}>
          <div>
            <Tag color="default" style={{ minWidth: 90, textAlign: "center" }}>GATE OFF</Tag>
            {" "}Gate inactive — no prompts shown regardless of version.
          </div>
          <div>
            <Tag color="green" style={{ minWidth: 90, textAlign: "center" }}>UP TO DATE</Tag>
            {" "}User version ≥ Latest version. Nothing shown.
          </div>
          <div>
            <Tag color="orange" style={{ minWidth: 90, textAlign: "center" }}>SOFT UPDATE</Tag>
            {" "}User version &lt; Latest but ≥ Min required. Dismissible prompt shown once per version.
          </div>
          <div>
            <Tag color="red" style={{ minWidth: 90, textAlign: "center" }}>HARD BLOCK</Tag>
            {" "}User version &lt; Min required. App fully blocked until user updates.
          </div>
        </div>
      </div>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16, maxWidth: 750 }}
        message="Min required version hard-blocks users — they cannot use the app at all until they update. Only raise it when you are sure old builds must stop working."
      />

      {loading ? (
        <Spin />
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 24,
            maxWidth: 1100,
            alignItems: "stretch",
          }}
        >
          {PLATFORMS.map((p) => renderPlatformCard(p.key, p.label))}
        </div>
      )}

      <div
        className="card"
        style={{ padding: 16, background: "white", maxWidth: 980, marginTop: 8 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h5 style={{ margin: 0 }}>History</h5>
          <Button
            size="small"
            onClick={() => loadHistory(historyPage)}
            loading={historyLoading}
          >
            Refresh
          </Button>
        </div>
        <Table
          rowKey={(r) => r._id}
          size="small"
          loading={historyLoading}
          columns={HISTORY_COLUMNS}
          dataSource={history}
          pagination={{
            current: historyPage,
            pageSize: 20,
            total: historyTotal,
            showSizeChanger: false,
            onChange: (p) => loadHistory(p),
          }}
        />
      </div>
    </>
  );
};

export default AppVersionTab;
